(() => {
  'use strict';

  const ROOT_SELECTOR = '.dhi-product[class*="-preview"]';
  const MEDIA_SELECTOR = ':scope > .dhi-product__media';
  const HERO_IMAGE_SELECTOR = [
    '.dhi-product__media--lead img',
    '.dhi-mt07-v2__media--hero img',
    '.dhi-mt12-preview [data-colour="brown"] img'
  ].join(',');
  const ROW_CLASSES = [
    'dhi-product__media--row-left',
    'dhi-product__media--row-right',
    'dhi-product__media--row-tail'
  ];

  function hasLegacyMeasuredHeight(gallery) {
    return Boolean(
      gallery.style.getPropertyValue('min-height') ||
      gallery.style.getPropertyValue('height') ||
      gallery.style.getPropertyValue('max-height')
    );
  }

  function clearLegacyMeasuredHeight(gallery) {
    gallery.style.removeProperty('min-height');
    gallery.style.removeProperty('height');
    gallery.style.removeProperty('max-height');
  }

  function stabilizeImageDimensions(image) {
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      if (!image.hasAttribute('width')) image.setAttribute('width', String(image.naturalWidth));
      if (!image.hasAttribute('height')) image.setAttribute('height', String(image.naturalHeight));
    }
  }

  function prepareHeroImages(root) {
    let priorityAssigned = false;
    root.querySelectorAll(HERO_IMAGE_SELECTOR).forEach((image) => {
      const hidden = Boolean(image.closest('[hidden]'));
      if (!hidden && !priorityAssigned) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
        priorityAssigned = true;
      } else {
        image.loading = 'lazy';
        image.fetchPriority = 'auto';
      }
      image.decoding = 'async';
      stabilizeImageDimensions(image);
      if (!image.complete) {
        image.addEventListener('load', () => stabilizeImageDimensions(image), { once: true });
      }
    });
  }

  function lockGalleryStage(root) {
    const galleries = Array.from(root.querySelectorAll('.dhi-product__gallery, .dhi-mt07-v2__gallery'));
    if (!galleries.length) return;
    if (!window.matchMedia('(min-width:1024px)').matches) {
      galleries.forEach((gallery) => gallery.style.removeProperty('--dhi-gallery-min-height'));
      return;
    }
    const visible = galleries.find((gallery) => !gallery.hidden && gallery.offsetParent !== null) || galleries[0];
    const measured = Math.ceil(visible.scrollHeight || 0);
    const previous = Number(root.dataset.dhiGalleryMinHeight || 0);
    const locked = Math.max(previous, measured);
    if (!locked) return;
    root.dataset.dhiGalleryMinHeight = String(locked);
    galleries.forEach((gallery) => gallery.style.setProperty('--dhi-gallery-min-height', locked + 'px'));
  }

  function installColourPreload(root) {
    if (root.dataset.dhiColourPreload === 'true') return;
    root.dataset.dhiColourPreload = 'true';
    const preload = (target) => {
      const button = target.closest && target.closest('[data-colour-choice]');
      if (!button || !root.contains(button)) return;
      const key = button.dataset.colourChoice;
      if (!key) return;
      const escape = window.CSS && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
      const image =
        root.querySelector('[data-colour="' + escape + '"] img') ||
        root.querySelector('[data-gallery-group="' + escape + '"] img');
      if (!image) return;
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decoding = 'async';
    };
    root.addEventListener('pointerover', (event) => preload(event.target), { passive: true });
    root.addEventListener('focusin', (event) => preload(event.target));
    root.addEventListener('click', (event) => preload(event.target), true);
  }

  function installSwitchStability(root) {
    if (root.dataset.dhiSwitchStability === 'true') return;
    root.dataset.dhiSwitchStability = 'true';

    root.addEventListener('click', (event) => {
      const button = event.target.closest && event.target.closest('[data-colour-choice]');
      if (!button || !root.contains(button)) return;

      const savedX = window.scrollX;
      const savedY = window.scrollY;
      lockGalleryStage(root);

      requestAnimationFrame(() => {
        lockGalleryStage(root);
        window.scrollTo({ left: savedX, top: savedY, behavior: 'auto' });
        requestAnimationFrame(() => {
          lockGalleryStage(root);
          window.scrollTo({ left: savedX, top: savedY, behavior: 'auto' });
        });
      });
    }, true);
  }

  function prewarmAlternateHeroes(root) {
    if (root.dataset.dhiHeroPrewarmed === 'true') return;
    root.dataset.dhiHeroPrewarmed = 'true';

    const warm = () => {
      root.querySelectorAll(HERO_IMAGE_SELECTOR).forEach((image, index) => {
        if (index === 0 || !image.src) return;
        const probe = new Image();
        probe.decoding = 'async';
        probe.fetchPriority = 'low';
        if (image.srcset) probe.srcset = image.srcset;
        if (image.sizes) probe.sizes = image.sizes;
        probe.src = image.currentSrc || image.src;
      });
    };

    const scheduleWarm = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 1500 });
      } else {
        window.setTimeout(warm, 900);
      }
    };

    if (document.readyState === 'complete') {
      scheduleWarm();
    } else {
      window.addEventListener('load', scheduleWarm, { once: true });
    }
  }

  function rebalanceGallery(gallery) {
    const media = Array.from(gallery.querySelectorAll(MEDIA_SELECTOR));

    media.forEach((item) => {
      ROW_CLASSES.forEach((className) => item.classList.remove(className));
    });

    const visibleSecondary = media.filter((item) => {
      return !item.hidden && !item.classList.contains('dhi-product__media--lead');
    });

    visibleSecondary.forEach((item, index) => {
      item.classList.add(
        index % 2 === 0
          ? 'dhi-product__media--row-left'
          : 'dhi-product__media--row-right'
      );
    });

    if (visibleSecondary.length % 2 === 1) {
      const tail = visibleSecondary[visibleSecondary.length - 1];
      tail.classList.remove('dhi-product__media--row-left');
      tail.classList.add('dhi-product__media--row-tail');
    }

    gallery.dataset.dhiGeometryStable = 'true';
  }

  function observeGallery(gallery, root) {
    if (gallery.dataset.dhiGeometryObserved === 'true') return;
    gallery.dataset.dhiGeometryObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      const geometryChanged = mutations.some((mutation) => {
        if (mutation.type === 'childList') return true;
        if (mutation.type !== 'attributes') return false;
        if (mutation.attributeName === 'hidden') return true;
        if (
          mutation.attributeName === 'style' &&
          mutation.target === gallery &&
          hasLegacyMeasuredHeight(gallery)
        ) return true;
        return false;
      });

      if (geometryChanged) {
        prepareHeroImages(root);
        lockGalleryStage(root);
        rebalanceGallery(gallery);
      }
    });

    observer.observe(gallery, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'style']
    });
  }

  function stabilizeRoot(root) {
    prepareHeroImages(root);
    installColourPreload(root);
    installSwitchStability(root);
    prewarmAlternateHeroes(root);
    lockGalleryStage(root);

    root.querySelectorAll('.dhi-product__gallery, .dhi-mt07-v2__gallery').forEach((gallery) => {
      rebalanceGallery(gallery);
      observeGallery(gallery, root);
    });

    root.classList.add('dhi-product--geometry-stable');
  }

  function stabilizeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) stabilizeRoot(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(stabilizeRoot);
  }

  function init() {
    stabilizeAll();

    window.addEventListener('load', () => stabilizeAll(), { once: true });
    window.addEventListener('resize', () => stabilizeAll(), { passive: true });

    document.addEventListener('shopify:section:load', (event) => {
      stabilizeAll(event.target);
    });

    document.addEventListener('shopify:section:reorder', () => stabilizeAll());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
